import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API } from '@configs/api.config';
import { JobFile } from '@core/models/job-file';
import { User } from '@core/models/user.model';
import { GraphqlClientService } from '@core/services/graphql-client.service';
import { JobFileCreationDTO } from '@shared/models/job-file-creation.model';
import { Pageable } from '@shared/models/pageable.model';
import { JobGroup } from 'app/modules/job-group/models/job-group.model';
import { Operation } from 'app/modules/organization/models/operation';
import { InvoiceFinancialQueryInput, InvoiceFinancialResult } from 'app/modules/dashboard/models/invoice-financial.model';
import { Observable, of } from 'rxjs';
import { LoggerService } from './logger.service';
import { SharedJobCount } from '../models/shared-job-count.model';
import { SharedJobsCountRequest, SharedJobsFetchRequest } from '../models/shared-job-fetch-request.model';
import { SharedJob } from '../models/shared-job.model';
import { TaskFeedSummary } from '../models/state/task-feed-summary.model';
import { EntityMap } from '@shared/models/types';
import { OnGoingJob } from '../models/on-going-job.model';
import { CustomersWithHighestRevenueComponent } from '../components/bubble-cloud/bubble-cloud.component';
import { PieChartComponent } from '../components/pie-chart/pie-chart.component';
import { JobSummaryComponent } from '../../wizard/components/job-summary/job-summary.component';
import { JobSummeryComponent } from '../components/job-summery/job-summery.component';
import { DashboardRow } from '../models/dashboard-rows.model';
import { Customer } from '../models/customer.model';
import { SupplierTripsFilterInput, SupplierTripsResponse } from '../models/supplier-trips.model';
import {
  SupplierCostAnalysisFilterInput,
  SupplierCostAnalysisResponse
} from '../models/supplier-costs.model';
import { catchError, map, take, tap } from 'rxjs/operators';
import { JobSummaryRequest } from '../models/job-summary.model';
import { JobSummaryResponse } from '../models/job-summary.model';
import { CustomerTripsFilterInput, CustomerTripsResponse } from '../models/customer-trips.model';
import {
  CustomerKilometersFilterInput,
  CustomerKilometersResponse,
  BackendCustomerKilometersResponse,
  BackendCustomerKilometerItem,
  CustomerKilometerItem
} from '../models/customer-kilometers.model';
import { OrgDetails } from '@shared/models/org-details.model';
import {
  getInvoiceFinancialData,
  getSupplierTrips,
  getSupplierCostAnalysis,
  getCustomerTrips,
  getCustomerKilometers,
  getTotalValuePerKmOrShipment,
  getCustomersMostProfiable,
  getUtilisationChartData,
  getSupplierList,
  getSupplierTimeSeries,
  getTimeInsightsData,
  getJobSummary,
  getCustomerRevenue
} from '../queries/dashboard.queries';
import {
  SupplierListResponse,
  SupplierTimeSeriesResponse,
  SupplierListFilterInput,
  SupplierTimeSeriesFilterInput
} from '../models/supplier-list.model';
import { ValuePerKmOrShipmentQueryInput, ValuePerKmOrShipmentResult } from '../models/value-per-km-or-shipment.types';
import { VehicleUtilizationMetric } from '../models/vehicle-utilization-metric.model';
import { GpsDevice } from 'app/modules/gps/models/gps-device.model';
import { TimeInsightsInput, TimeInsightsResult } from '../models/time-insights.model';

@Injectable()
export class DashBoardService {
  private readonly endpoint = 'invoice-endpoint';
  private readonly reportEndpoint = 'report-endpoint';
  constructor(
    private httpClient: HttpClient,
    private gqlService: GraphqlClientService,
    private logger: LoggerService
  ) {}

  fetchOngoingJobs(orgId: string, userId: string, pageNum: number, pageSize: number, searchText = 'empty'): Observable<Pageable<JobFile>> {
    const url = API.jobFile.getOngoingJobs.format([
      orgId,
      userId,
      pageNum.toString(),
      pageSize.toString(),
      searchText,
    ]);
    return this.httpClient.get<Pageable<JobFile>>(url);
  }

  fetchTaskFeedJobById(orgId: string, jobRefId: string) {
    const url = API.jobFile.getTaskFeedJobByJobRefId.format([
      orgId,
      jobRefId
    ]);
    return this.httpClient.get<OnGoingJob>(url);
  }

  fetchTaskFeedSummary(orgId: string, userId: string): Observable<TaskFeedSummary> {
    const url = API.jobFile.getTaskFeedJobCount.format([orgId, userId]);
    return this.httpClient.get<TaskFeedSummary>(url);
  }

  fetchUsersByScopes(scopes: string[], pageNum: number, pageSize: number, searchKey?: string): Observable<Pageable<User>> {
    let url = API.organization.getOrgUserByScopes.format([`${pageNum}`, `${pageSize}`]);

    if (searchKey) {
      url = `${url}&searchKey=${searchKey}`;
    }
    return this.httpClient.post<Pageable<User>>(url, scopes);
  }

  getOrLogsByOrgIds(orgIds: string[]): Observable<EntityMap<string, string>> {
    const url = API.organization.getCollabLogoMap;
    return this.httpClient.post<EntityMap<string, string>>(url, orgIds);
  }

  createJobFile(jobFileCreationModel: JobFileCreationDTO): Observable<JobFileCreationDTO> {
    const url = API.jobFile.createJobFile;
    return this.httpClient.post<JobFileCreationDTO>(url, jobFileCreationModel);
  }

  createJobFileV2(jobFileCreationModel: JobFileCreationDTO): Observable<JobFileCreationDTO> {
    const url = API.jobFile.createJobFileV2;
    return this.httpClient.post<JobFileCreationDTO>(url, jobFileCreationModel);
  }

  /**
   * Attaches existing shipment orders to a job. The backend populates jobFile.shipmentOrderSpecs,
   * de-dupes, and moves each order to INPROGRESS/PLANNING under the given job.
   */
  mergeShipmentOrdersToJobByIds(orgId: string, jobRefId: string, shipmentOrderSpecIds: string[]): Observable<unknown> {
    const url = API.jobFile.mergeShipmentOrdersToJob.format([orgId, jobRefId]);
    return this.httpClient.put(url, shipmentOrderSpecIds);
  }

  getJobGroups(orgId: string, pageNum: number, pageSize: number, searchKey = ''): Observable<Pageable<JobGroup>> {
    const url = API.jobFile.getJobGroups.format([orgId, `${pageNum}`, `${pageSize}`]);
    const newUrl = `${url}?searchKey=${searchKey}`;
    return this.httpClient.get<Pageable<JobGroup>> (newUrl);
  }

  createJobGroup(jb: JobGroup): Observable<JobGroup> {
    const url = API.jobFile.createJobGroup;
    return this.httpClient.post<JobGroup>(url, jb);
  }

  loadSharedJobs(requestModel: SharedJobsFetchRequest): Observable<Pageable<SharedJob>> {
    const url = API.jpm.loadSharedJobs.format([]);
    return this.httpClient.post<Pageable<SharedJob>>(url, requestModel);
  }

  loadSharedJobCount(requestModel: SharedJobsCountRequest): Observable<SharedJobCount> {
    const url = API.jpm.loadSharedJobCounts.format([]);
    return this.httpClient.post<SharedJobCount>(url, requestModel);
  }

  getOrgDetails(orgIds: string[]): Observable<OrgDetails[]> {
    const url = API.organization.getOrgDetails;
    return this.httpClient.post<OrgDetails[]>(url, orgIds);
  }

  saveDashboardRows(orgId: string, userId: string, dashboardRows: any[]): Observable<any> {
    const url = API.dashboard.saveDashboardRows.format([orgId, userId]);
    return this.httpClient.post(url, { dashboardRows });
  }

  fetchHighestRevenuedCustomers(fromDate: Date, toDate: Date, orgId: string, operationId: string): Observable<Customer[]> {
    const variables = {
      input: {
        orgId,
        operationId: operationId || 'ALL',
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString()
      }
    };

    return this.gqlService.queryDataFromGQL<any>(
      variables,
      getCustomerRevenue,
      'customerRevenue',
      'report-endpoint'
    ).pipe(
      map(response => {
        const data = response as any;
        if (data?.error) {
          this.logger.error('Customer revenue GraphQL error:', data.error);
          return [];
        }
        return data?.customers?.map((customer: any) => ({
          customerId: customer.customerId,
          totalRevenue: customer.totalRevenue,
          previousTotalRevenue: customer.previousTotalRevenue,
          percentage: customer.percentage
        })) || [];
      }),
      catchError(error => {
        this.logger.error('Error fetching customer revenue via GraphQL:', error);
        // Fallback to REST API if GraphQL fails
        const url = API.report.fetchHighestRevenuedCustomers;
        const params = new HttpParams()
          .set('fromDate', fromDate.toISOString())
          .set('toDate', toDate.toISOString())
          .set('orgId', orgId)
          .set('operationId', operationId);
        return this.httpClient.get<Customer[]>(url, { params });
      })
    );
  }

  getOrgDetailsLogos(orgIds: string[]): Observable<OrgDetails[]> {
    const url = API.organization.getOrgDetails;
    return this.httpClient.post<any[]>(url, orgIds);
  }

  fetchInvoiceFinancialData(variables: InvoiceFinancialQueryInput): Observable<InvoiceFinancialResult> {
    return this.gqlService.queryDataFromGQL<InvoiceFinancialResult>(
      variables,
      getInvoiceFinancialData,
      'getInvoiceFinancialData',
      this.reportEndpoint
    );
  }

  fetchTotalValuePerKmOrShipment(variables: ValuePerKmOrShipmentQueryInput): Observable<ValuePerKmOrShipmentResult> {
    return this.gqlService.queryDataFromGQL<ValuePerKmOrShipmentResult>(
      variables,
      getTotalValuePerKmOrShipment,
      'getTotalValuePerKmOrShipment',
      this.reportEndpoint
    );
  }

  fetchSupplierTrips(filter: SupplierTripsFilterInput): Observable<SupplierTripsResponse> {
    return this.gqlService.queryDataFromGQL<SupplierTripsResponse>(
      filter,
      getSupplierTrips,
      'supplierTrips',
      this.reportEndpoint
    );
  }

  fetchCustomerTrips(filter: CustomerTripsFilterInput): Observable<CustomerTripsResponse> {
    return this.gqlService.queryDataFromGQL<CustomerTripsResponse>(
      filter,
      getCustomerTrips,
      'customerTrips',
      this.reportEndpoint
    );
  }

  fetchAllJobSummary(orgId: string, request: JobSummaryRequest): Observable<JobSummaryResponse> {
    const url = API.report.getAllJobSummary.format([orgId]);
    return this.httpClient.post<JobSummaryResponse>(url, request).pipe(
      map(response => this.transformDates(response)),
      catchError(error => {
        this.logger.error('Error fetching job summary:', error);
        return of({
          pagination: {
            totalItems: 0,
            itemsPerPage: request.pageSize || 10,
            currentPage: request.pageIndex || 0,
            totalPages: 0
          },
          error: 'Error fetching job summary data: ' + error.message
        });
      })
    );
  }

  fetchJobSummaryGraphQL(request: JobSummaryRequest): Observable<JobSummaryResponse> {
    const timeRangeMapping: { [key: string]: string } = {
      '24Hours': 'HOURS_24',
      '7Days': 'DAYS_7',
      '30Days': 'DAYS_30',
      '12Months': 'MONTHS_12',
      'Custom': 'CUSTOM'
    };

    const statusMapping: { [key: string]: string } = {
      'Completed': 'COMPLETED',
      'DeliveryInProgress': 'DELIVERY_IN_PROGRESS',
      'Notified': 'NOTIFIED',
      'Contracted': 'CONTRACTED',
      'Planned': 'PLANNED',
      'LoadAssignment': 'LOAD_ASSIGNMENT',
      'PendingAcceptance': 'PENDING_ACCEPTANCE',
      'Accepted': 'ACCEPTED',
      'Terminated': 'TERMINATED'
    };

    const input: any = {
      orgId: request.orgId,
      operationId: request.operationId,
      timeRange: timeRangeMapping[request.timeRange] || 'DAYS_7',
      pageIndex: request.pageIndex,
      pageSize: request.pageSize,
      includeAllWorkOrders: false
    };

    if (request.status) {
      input.status = statusMapping[request.status];
    }

    if (request.startDate) {
      input.startDate = request.startDate.toISOString();
    }

    if (request.endDate) {
      input.endDate = request.endDate.toISOString();
    }

    if (request.sortBy) {
      input.sortBy = request.sortBy;
    }

    if (request.sortDirection) {
      input.sortDirection = request.sortDirection;
    }

    return this.gqlService.queryDataFromGQL<JobSummaryResponse>(
      { input },
      getJobSummary,
      'jobSummary',
      this.reportEndpoint
    ).pipe(
      // One answer per request: completes the request (so a newer one can cancel it and the prefetch
      // can chain requests) and releases the watch query instead of keeping it alive.
      take(1),
      map(response => this.transformDates(response)),
      catchError(error => {
        this.logger.error('Error fetching job summary via GraphQL:', error);
        return of({
          pagination: {
            totalItems: 0,
            itemsPerPage: request.pageSize || 10,
            currentPage: request.pageIndex || 0,
            totalPages: 0
          },
          error: 'Error fetching job summary data: ' + error.message
        });
      })
    );
  }

  fetchJobSummaryByStatus(orgId: string, request: JobSummaryRequest): Observable<JobSummaryResponse> {
    const url = API.report.getJobSummaryByStatus.format([orgId]);
    let params = new HttpParams()
      .set('timeRange', request.timeRange)
      .set('pageIndex', request.pageIndex.toString())
      .set('pageSize', request.pageSize.toString());

    if (request.startDate) {
      params = params.set('startDate', request.startDate.toISOString());
    }
    if (request.endDate) {
      params = params.set('endDate', request.endDate.toISOString());
    }

    return this.httpClient.get<JobSummaryResponse>(url, { params }).pipe(
      map(response => this.transformDates(response)),
      catchError(error => {
        this.logger.error('Error fetching job summary by status:', error);
        return of({
          pagination: {
            totalItems: 0,
            itemsPerPage: request.pageSize || 10,
            currentPage: request.pageIndex || 0,
            totalPages: 0
          },
          error: 'Error fetching job summary data: ' + error.message
        });
      })
    );
  }

  transformDates(response: JobSummaryResponse): JobSummaryResponse {
    const transformJobDates = (jobs: any[]) =>
      jobs?.map(job => ({
        ...job,
        createdDateTime: new Date(job.createdDateTime),
        expectedStartTime: job.expectedStartTime ? new Date(job.expectedStartTime) : undefined,
        expectedEndTime: job.expectedEndTime ? new Date(job.expectedEndTime) : undefined,
        terminatedAt: job.terminatedAt ? new Date(job.terminatedAt) : undefined
      })) || [];

    return {
      ...response,
      completedJobs: transformJobDates(response.completedJobs),
      deliveryInProgressJobs: transformJobDates(response.deliveryInProgressJobs),
      notifiedJobs: transformJobDates(response.notifiedJobs),
      contractedJobs: transformJobDates(response.contractedJobs),
      plannedJobs: transformJobDates(response.plannedJobs),
      loadAssignmentJobs: transformJobDates(response.loadAssignmentJobs),
      pendingAcceptanceJobs: transformJobDates(response.pendingAcceptanceJobs),
      acceptedJobs: transformJobDates(response.acceptedJobs),
      terminatedJobs: transformJobDates(response.terminatedJobs)
    };
  }

  fetchMostProfitableCustomers(variables: { orgId: string; operationId: string; fromDate: Date; toDate: Date; pageSize: number }) {
    return this.gqlService.queryDataFromGQL<any>(
      variables,
      getCustomersMostProfiable,
      'getCustomersMostProfiable',
      this.reportEndpoint
    );
  }

  getAllActiveGpsDevices(): Observable<GpsDevice[]> {
    const url = API.fmGPS.getAllActiveDevices;
    return this.httpClient.get<GpsDevice[]>(url);
  }

  fetchUtilisationChartData(orgId: string, operationId: string, startDate: Date, endDate: Date): Observable<VehicleUtilizationMetric[]> {
    return this.gqlService.queryDataFromGQL<any>(
      { orgId, operationId, startDate: startDate.toISOString(), endDate: endDate.toISOString() },
      getUtilisationChartData,
      'utilisationChartData',
      this.reportEndpoint
    );
  }

  fetchCustomerKilometers(filter: CustomerKilometersFilterInput): Observable<CustomerKilometersResponse> {
    const backendFilter = {
      orgId: filter.orgId,
      operationId: filter.operationId,
      startDate: filter.startDate.toISOString(),
      endDate: filter.endDate.toISOString()
    };

    return this.gqlService.queryDataFromGQL<any>(
      { filter: backendFilter },
      getCustomerKilometers,
      'customerKilometers',
      this.reportEndpoint
    ).pipe(
      map(backendResponse => this.transformCustomerKilometersResponse(backendResponse)),
      catchError(error => {
        this.logger.error('Error fetching customer kilometers:', error);
        return of({
          customerKilometers: {
            customers: []
          }
        });
      })
    );
  }

  fetchSupplierCostAnalysis(filter: SupplierCostAnalysisFilterInput): Observable<SupplierCostAnalysisResponse> {
    return this.gqlService.queryDataFromGQL<SupplierCostAnalysisResponse>(
      { filter },
      getSupplierCostAnalysis,
      'supplierCostAnalysis',
      this.reportEndpoint
    ).pipe(
      tap(response => this.logger.log('Service: Raw GraphQL response:', response))
    );
  }

  fetchSupplierList(filter: SupplierListFilterInput): Observable<SupplierListResponse> {
    return this.gqlService.queryDataFromGQL<SupplierListResponse>(
      { filter },
      getSupplierList,
      'supplierList',
      this.reportEndpoint
    );
  }


  fetchSupplierTimeSeries(filter: SupplierTimeSeriesFilterInput): Observable<SupplierTimeSeriesResponse> {
    return this.gqlService.queryDataFromGQL<SupplierTimeSeriesResponse>(
      { filter },
      getSupplierTimeSeries,
      'supplierTimeSeries',
      this.reportEndpoint
    );
  }

  private transformCustomerKilometersResponse(backendResponse: any): CustomerKilometersResponse {
    this.logger.log('Service: Raw backend response:', backendResponse);

    let customersData: any[] = [];

    if (backendResponse.customers) {
      customersData = backendResponse.customers;
      this.logger.log('Service: Found customers data directly:', customersData.length);
    } else if (backendResponse.data?.customerKilometers?.customers) {
      customersData = backendResponse.data.customerKilometers.customers;
      this.logger.log('Service: Found customers data in GraphQL structure:', customersData.length);
    } else if (backendResponse.customerKilometers?.customers) {
      customersData = backendResponse.customerKilometers.customers;
      this.logger.log('Service: Found customers data in intermediate structure:', customersData.length);
    }

    if (!customersData || customersData.length === 0) {
      this.logger.log('Service: No customers data found in response');
      return {
        customerKilometers: {
          customers: []
        }
      };
    }

    const transformedCustomers: CustomerKilometerItem[] = customersData.map(backendItem => ({
      customerId: backendItem.customerId,
      customerName: backendItem.customerName,
      totalKilometers: backendItem.totalKilometers,
      logo: 'assets/images/no_profile_image.png', // Default logo since not provided by backend
      timeSeries: this.generateMockTimeSeries(backendItem.totalKilometers),
      vehicleTypeBreakdown: backendItem.vehicleData?.map(vehicleData => ({
        vehicleType: vehicleData.vehicleCategoryName,
        kilometers: vehicleData.kilometers
      })) || []
    }));

    const transformedResponse = {
      customerKilometers: {
        customers: transformedCustomers
      }
    };

    this.logger.log('Service: Transformed response:', transformedResponse);
    this.logger.log('Service: Transformed customers count:', transformedCustomers.length);
    return transformedResponse;
  }

  private generateMockTimeSeries(totalKilometers: number): Array<{timeGroup: string; kilometers: number}> {
    const timeSeries = [];
    const now = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const timeGroup = date.toISOString().split('T')[0]; // YYYY-MM-DD format
      const kilometers = Math.round((totalKilometers / 7) * (0.5 + Math.random())); // Random distribution
      timeSeries.push({ timeGroup, kilometers });
    }

    return timeSeries;
  }

  fetchTimeInsightsData(input: TimeInsightsInput): Observable<TimeInsightsResult> {
    const variables = {
      orgId: input.orgId,
      operationId: input.operationId,
      startDate: input.startDate,
      endDate: input.endDate,
      periodType: input.periodType
    };

    return this.gqlService.queryDataFromGQL<TimeInsightsResult>(
      variables,
      getTimeInsightsData,
      'timeInsights',
      this.reportEndpoint
    ).pipe(
      map(response => {
        if (!response || !response.financial || !response.metrics) {
          throw new Error('Invalid time insights response structure');
        }
        
        const result: TimeInsightsResult = {
          financial: response.financial,
          metrics: response.metrics,
          metadata: response.metadata || {
            executionTime: 0,
            cacheHit: false,
            dataFreshness: new Date(),
            queryComplexity: 1
          }
        };

        return result;
      }),
      catchError(error => {
        this.logger.error('Error fetching time insights data:', error);
        throw error;
      })
    );
  }

  getPartnerOrganizationsByOperations(): Observable<any[]> {
    const url = API.organization.getPartnerOrganizationsByOperations;
    return this.httpClient.get<any[]>(url);
  }

  getOnBoardedOrganizations(): Observable<any[]> {
    const url = API.organization.getOnBoardedOrganizations;
    return this.httpClient.get<any[]>(url);
  }

  getPartnerOrganizationsForBehalf(pageNum: number = 0, pageSize: number = 50, searchKey?: string): Observable<Pageable<{id: string, orgName: string}>> {
    let url = `${API.organization.getPartnerOrganizationsForBehalf}?pageNum=${pageNum}&pageSize=${pageSize}`;
    if (searchKey) {
      url += `&searchKey=${encodeURIComponent(searchKey)}`;
    }
    return this.httpClient.get<Pageable<{id: string, orgName: string}>>(url);
  }

  getOnBoardedOrganizationsForBehalf(pageNum: number = 0, pageSize: number = 50, searchKey?: string): Observable<Pageable<{id: string, orgName: string}>> {
    let url = `${API.organization.getOnBoardedOrganizationsForBehalf}?pageNum=${pageNum}&pageSize=${pageSize}`;
    if (searchKey) {
      url += `&searchKey=${encodeURIComponent(searchKey)}`;
    }
    return this.httpClient.get<Pageable<{id: string, orgName: string}>>(url);
  }

  createOperation(operation: Operation): Observable<Operation> {
    const url = API.organization.updateAndAddOperation;
    return this.httpClient.put<Operation>(url, operation);
  }

}
